// Helpers
import { actionCreator, getLocale } from '../../../helpers/utils';

import { PRODUCT_IDS } from '../../../helpers/enums';
import Resources from '../../../helpers/resources';

export const ADD_NEW_GROUP = 'ADD_NEW_GROUP';
export const EDIT_GROUP_NAME = 'EDIT_GROUP_NAME';
export const DELETE_EXISTING_GROUP = 'DELETE_EXISTING_GROUP';
export const EDIT_GROUP_COURSE = 'EDIT_GROUP_COURSE';
export const UPDATE_GROUP_SELECTION = 'UPDATE_GROUP_SELECTION';
export const RESET_EDIT_INFORMATION = 'RESET_EDIT_INFORMATION';

// EDIT GROUP
export const GET_DEFAULT_COURSE = 'GET_DEFAULT_COURSE';
export const EDIT_GROUP_INFORMATION_START = 'EDIT_GROUP_INFORMATION_START';
export const EDIT_GROUP_INFORMATION_END = 'EDIT_GROUP_INFORMATION_END';

export const CREATE_MULTI_YEAR_GROUPS = 'CREATE_MULTI_YEAR_GROUPS';
export const UPDATE_MULTI_YEAR_GROUPS_CREATED_COUNT = 'UPDATE_MULTI_YEAR_GROUPS_CREATED_COUNT';
export const RESET_MULTI_YEAR_GROUPS_FETCH = 'RESET_MULTI_YEAR_GROUPS_FETCH';

export const DEFAULT_NEW_COURSE_ID = '00000000-0000-0000-0000-000000000000';
export const NO_UPF_COURSE_FOUND_ID = '0';

// Add Group
const addNewGroup = ({ classroomId, groupName, courseId, upfCourseId, newCourseId }) => async (dispatch) => {
    if (Resources.addNewGroup) {
        const UPFCourseId = typeof upfCourseId === 'number' ? upfCourseId : -1;
        const newCourseID = newCourseId || DEFAULT_NEW_COURSE_ID;
        const addNewGroupResponse = await Resources.addNewGroup
            .post({ classroomId })
            .headers({ Locale: getLocale() })
            .withBody({})
            .withParams({
                name: groupName,
                courseId,
                upfCourseId: UPFCourseId,
                conquestCourseId: newCourseID,
                useUpfSchoolDefault: UPFCourseId === -1
            })
            .send();

        if (addNewGroupResponse.valid) {
            const addGroupAction = actionCreator(ADD_NEW_GROUP, {
                data: addNewGroupResponse.data
            });
            dispatch(addGroupAction);

            // Valid return
            return addNewGroupResponse;
        }

        return addNewGroupResponse;
    }

    return false;
};

// Edit Group name
const editGroupName = ({ classroomId, groupId, groupName }) => async (dispatch) => {
    if (Resources.editGroupCourses) {
        const editGroupNameResponse = await Resources.editGroupCourses
            .put({ classroomId, groupId })
            .headers({ Locale: getLocale() })
            .withBody({
                name: groupName
            })
            // Prevent a .raw() chain method so that errors can be handled properly
            //      -> although the user will get a console error, it's a trade off we need
            .send();

        if (editGroupNameResponse.valid) {
            const editGroupAction = actionCreator(EDIT_GROUP_NAME, {
                data: editGroupNameResponse.data
            });
            dispatch(editGroupAction);
            // Valid return
            return editGroupNameResponse;
        }
        return editGroupNameResponse;
    }

    return false;
};

// Delete Group
const deleteExistingGroup = ({ classroomId, groupId }) => async (dispatch) => {
    if (Resources.deleteGroup) {
        const deleteGroupResponse = await Resources.deleteGroup
            .delete({ classroomId, groupId })
            .headers({ Locale: getLocale() })
            .raw()
            .send();

        if (deleteGroupResponse.valid) {
            const deleteGroupAction = actionCreator(DELETE_EXISTING_GROUP, {
                data: deleteGroupResponse.data
            });
            dispatch(deleteGroupAction);

            return true;
        }

        return deleteGroupResponse;
    }

    return false;
};

// Edit Group Course
/**
 * prerequisite:
 * - requires to have information about the courseId, which is in courses-actions.js
 * @param classroomId
 * @param groupId
 * @param courseId
 */
const editGroupCourse = ({ classroomId, groupId, courseId, upfCourseId, newCourseId }) => async (dispatch) => {
    if (Resources.editGroupCourses) {
        const UPFCourseId = typeof upfCourseId === 'number' ? upfCourseId : 0;
        const newCourseID = newCourseId || DEFAULT_NEW_COURSE_ID;
        const editGroupCourseResponse = await Resources.editGroupCourses
            .put({ classroomId, groupId })
            .headers({ Locale: getLocale() })
            .withBody({
                courseId,
                upfCourseId: UPFCourseId,
                useUpfSchoolDefault: UPFCourseId === -1,
                conquestCourseId: newCourseID
            })
            .raw()
            .send();

        if (editGroupCourseResponse.valid) {
            const editGroupAction = actionCreator(EDIT_GROUP_COURSE, {
                data: editGroupCourseResponse.data
            });
            dispatch(editGroupAction);
            // Valid return
            return true;
        }
        return editGroupCourseResponse;
    }

    return false;
};

/** EDIT GROUP INFORMATION RETRIEVAL CODE
 * A list of requests trying to get information of the current groupd to get their course details
 * @param classroomId
 * @param groupId
 * @param productId
 */
const getEditGroupCourseInformation = ({
    classId,
    productId,
    groupId
}) => async (dispatch) => {
    /*
    Check if there's classroom groups for the current class,
    otherwise get the course details based on the group selected
     */
    if (Resources.classroomGroups) {
        const getGroupInformationStartAction = actionCreator(EDIT_GROUP_INFORMATION_START);
        await dispatch(getGroupInformationStartAction);

        const getGroupsResponse = await Resources.classroomGroups
            .get({
                classId,
                productId,
                groupId
            })
            .withParams({
                includeGlobalCourses: true
            })
            .headers({ Locale: getLocale() })
            .send();

        let isSchoolCourse;
        let groupCountryCode;
        if (getGroupsResponse.valid) {
            const {
                data: {
                    countries, yearGroups, courses, groupUpfCourse, groupCourse, upfCourses,
                    groupConquestCourse, conquestCourses, groupCountry, groupCourseYear
                }
            } = getGroupsResponse;

            const getCourseCountriesAction = actionCreator(GET_DEFAULT_COURSE, {
                countryList: countries,
                yearList: yearGroups,
                courseList: courses,
                groupUpfCourse,
                groupCourse,
                upfCourseList: productId === PRODUCT_IDS.Mathletics ? upfCourses : '',
                groupNewCourse: groupConquestCourse,
                newCourseList: conquestCourses,
                countryCode: groupCountry.code,
                year: groupCourseYear.yearGroupEnum
            });

            dispatch(getCourseCountriesAction);

            // Course data variables
            const getCourseData = getGroupsResponse.data;
            const courseId = getCourseData.groupCourse.id.toString();
            const yearGroup = getCourseData.groupCourseYear.yearGroupEnum.toString();
            const countryName = getCourseData.groupCountry.code.toString();
            const upfCourse = getCourseData.groupUpfCourse === null ? '-1' : getCourseData.groupUpfCourse.id.toString();
            const newCourse = getCourseData.groupConquestCourse === null ?
                DEFAULT_NEW_COURSE_ID : getCourseData.groupConquestCourse.id.toString();

            isSchoolCourse = getCourseData.isSchoolCourse;
            groupCountryCode = groupCountry.code;

            const courseInformation = {
                courseSelectValue: courseId,
                yearSelectValue: yearGroup,
                countrySelectValue: countryName,
                upfCourseSelectValue: upfCourse,
                newCourseSelectValue: newCourse
            };

            // eslint-disable-next-line max-len
            const { courseSelectValue, yearSelectValue, upfCourseSelectValue, newCourseSelectValue } = courseInformation;
            const countrySelectValue = isSchoolCourse ? '' : courseInformation.countrySelectValue;
            const groupUpfCourseOption = getCourseData.groupUpfCourse;
            const groupCourseOption = getCourseData.groupCourse;
            const groupNewCourseOption = getCourseData.groupConquestCourse;

            const getGroupInformationEndAction = actionCreator(EDIT_GROUP_INFORMATION_END, {
                data: {
                    countrySelectValue,
                    courseSelectValue,
                    groupCourseOption,
                    groupUpfCourseOption,
                    upfCourseSelectValue,
                    groupNewCourseOption,
                    newCourseSelectValue,
                    yearSelectValue,
                    groupCountryCode
                }
            });
            await dispatch(getGroupInformationEndAction);
        }

        return {
            isSchoolCoursesEnabled: isSchoolCourse,
            groupCountryCode
        };
    }

    return {
        isSchoolCoursesEnabled: undefined,
        groupCountryCode: ''
    };
};

/**
 * A list of requests trying to get information of the current groupd to get their course details
 * @param defaultData
 */
const getDefaultCourseInformation = ({ defaultData }) => async (dispatch) => {
    /*
    Check if there's classroom groups for the current class,
    otherwise get the course details based on the group selected
     */
    if (Resources.getDefaultCourse) {
        const getGroupInformationStartAction = actionCreator(EDIT_GROUP_INFORMATION_START);
        await dispatch(getGroupInformationStartAction);

        let isSchoolCourse;
        let groupCountryCode;

        if (defaultData) {
            // To display rest of the data in select box if any one of the data(Country/Year/Course) is missing from BE
            const countryName = defaultData.data.defaultCountry.code !== null ?
                defaultData.data.defaultCountry.code : '';
            const yearGroup = defaultData.data.defaultYear !== null ?
                defaultData.data.defaultYear.yearGroupEnum.toString() : '';
            const courseId = defaultData.data.defaultCourse !== null ?
                defaultData.data.defaultCourse.id.toString() : '';
            const conquestId = defaultData.data.defaultConquestCourse !== null ?
                defaultData.data.defaultConquestCourse.id : DEFAULT_NEW_COURSE_ID;
            isSchoolCourse = defaultData.data.isSchoolCourse;
            groupCountryCode = defaultData.data.defaultCountry.code;

            const courseInformation = {
                courseSelectValue: courseId,
                yearSelectValue: yearGroup,
                countrySelectValue: countryName,
                newCourseSelectValue: conquestId
            };

            // Set default country, year and course values
            const { courseSelectValue, yearSelectValue, newCourseSelectValue } = courseInformation;
            const countrySelectValue = isSchoolCourse ? '' : courseInformation.countrySelectValue;

            const getGroupInformationEndAction = actionCreator(EDIT_GROUP_INFORMATION_END, {
                data: {
                    courseSelectValue,
                    yearSelectValue,
                    countrySelectValue,
                    upfCourseSelectValue: '-1',
                    newCourseSelectValue,
                    groupCountryCode
                }
            });
            await dispatch(getGroupInformationEndAction);
        }

        return {
            isSchoolCoursesEnabled: isSchoolCourse,
            groupCountryCode
        };
    }

    return {
        isSchoolCoursesEnabled: undefined,
        groupCountryCode: ''
    };
};

const updateGroupCheckbox = ({ value, isSelected }) => async (dispatch) => {
    const updateGroupAction = actionCreator(UPDATE_GROUP_SELECTION, {
        value,
        isSelected
    });
    dispatch(updateGroupAction);
};

const updateStudentGroup = ({ newGroupId, userIds, classId }) => async (dispatch) => {
    if (Resources.assignGroup) {
        const changeGroupResponse = await Resources.assignGroup
            .patch({
                classId,
                groupId: newGroupId
            })
            .headers({ Locale: getLocale() })
            .withBody(userIds)
            .raw()
            .send();

        if (changeGroupResponse.valid) {
            const updateGroupAction = actionCreator('STUDENT_GROUP_CHANGED', {
                newGroupId,
                userIds
            });
            dispatch(updateGroupAction);
        }

        return changeGroupResponse.valid;
    }

    return false;
};

const resetEditInformationHandler = () => async (dispatch) => {
    const updateGroupAction = actionCreator(RESET_EDIT_INFORMATION);
    dispatch(updateGroupAction);
};

const resetMultiYearGroupFetch = () => async (dispatch) => {
    const updateGroupAction = actionCreator(RESET_MULTI_YEAR_GROUPS_FETCH);
    dispatch(updateGroupAction);
};

const createMultiYearGroups = ({ classId }) => async (dispatch) => {
    if (Resources.createMultiYearGroups) {
        const actionGroup = actionCreator(CREATE_MULTI_YEAR_GROUPS);
        await dispatch(actionGroup);

        const createMultiYearGroupsResponse = await Resources.createMultiYearGroups
            .post({ classId })
            .headers({ Locale: getLocale() })
            .withBody({})
            .withParams({
                productId: PRODUCT_IDS.Mathletics
            })
            .send();

        const multiYearGroupCountAction = actionCreator(UPDATE_MULTI_YEAR_GROUPS_CREATED_COUNT, {
            data: {
                count: createMultiYearGroupsResponse.data.createdGroupsCount
            }
        });
        await dispatch(multiYearGroupCountAction);
    }
};

export default {
    addNewGroup,
    editGroupName,
    deleteExistingGroup,
    editGroupCourse,
    getEditGroupCourseInformation,
    getDefaultCourseInformation,
    updateStudentGroup,
    updateGroupCheckbox,
    resetEditInformationHandler,
    createMultiYearGroups,
    resetMultiYearGroupFetch
};



// WEBPACK FOOTER //
// ./containers/console/actions/group-actions.js